// src/components/Tickets/TicketList.jsx
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import api from '../../services/api'
import Loader from '../Utils/Loader'
import Modal from '../common/Modal'
import { AuthContext } from '../../contexts/AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEnvelopeOpenText,
  faPaperPlane,
  faTimes,
  faRotate,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons'
import '../../css/tickets.css'

const fmt = (d) => (d ? new Date(d).toLocaleString('it-IT') : '—')
const STATUS = [
  { value: 'pending',   label: 'In attesa'    },
  { value: 'active',    label: 'Attivo'       },
  { value: 'in_review', label: 'In revisione' },
  { value: 'rejected',  label: 'Rifiutato'    },
  { value: 'open',      label: 'Aperto'       },
  { value: 'closed',    label: 'Chiuso'       },
]

const StatusPill = ({ value }) => {
  const label = STATUS.find((s) => s.value === value)?.label ?? value
  return <span className={`t-pill t-${value || 'pending'}`}>{label}</span>
}

const rowClassByStatus = (s) => `tickets-row body s-${s || 'pending'}`

export default function TicketList({ refreshKey, onChanged, initialTicketId }) {
  const { user, hasPermission } = useContext(AuthContext)

  const roles = user?.roles || []
  const hasRole = (name) => roles.some((r) => r.name === name)

  // isViewer = true solo se NON può rispondere (nemmeno ai propri)
  const isViewer = useMemo(() => {
    return !hasPermission('tickets_reply') && !hasPermission('tickets_reply_all')
  }, [hasPermission])

  const isSupport = useMemo(() => {
    if (!user) return false
    if (hasRole('admin') || hasRole('ticket_support')) return true
    return (
      hasPermission('tickets_reply_all') ||
      hasPermission('tickets_status_set_all') ||
      hasPermission('tickets_edit') ||
      hasPermission('tickets_destroy')
    )
  }, [user, hasPermission, roles])

  const canStatusAll = useMemo(
    () => !isViewer && (isSupport || hasPermission('tickets_status_set_all')),
    [isViewer, isSupport, hasPermission]
  )

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [sort, setSort] = useState({ key: null, dir: null })
  const [scopeAll, setScopeAll] = useState(false)

  // ---- CHAT SCROLL ----
  const chatRef = useRef(null)
  const bottomRef = useRef(null)
  const stickToBottomRef = useRef(true)   // true se l'utente è vicino al fondo
  const pendingScrollRef = useRef(false)  // true subito dopo invio mia risposta
  const initialOpenedRef = useRef(false)  // true se abbiamo già aperto il ticket iniziale

  const rafScrollBottomInstant = () => {
    const el = chatRef.current
    if (!el) return
    const doScroll = () => {
      const prev = el.style.scrollBehavior
      el.style.scrollBehavior = 'auto'
      el.scrollTop = el.scrollHeight
      el.style.scrollBehavior = prev || ''
    }
    // doppio rAF per aspettare DOM + layout delle bolle
    requestAnimationFrame(() => requestAnimationFrame(doScroll))
  }

  const rafScrollBottomSmooth = () => {
    const doScroll = () => bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
    requestAnimationFrame(() => requestAnimationFrame(doScroll))
  }

  const isNearBottom = (el, threshold = 60) =>
    el ? el.scrollHeight - el.scrollTop - el.clientHeight <= threshold : true

  const onThreadScroll = () => {
    const el = chatRef.current
    stickToBottomRef.current = isNearBottom(el)
  }

  const sortIcon = (key) => {
    if (sort.key !== key || !sort.dir) return faSort
    return sort.dir === 'asc' ? faSortUp : faSortDown
  }
  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' }
      if (s.dir === 'asc') return { key, dir: 'desc' }
      return { key: null, dir: null }
    })
  }

  const load = async (p = 1) => {
    setError('')
    try {
      setLoading(true)
      const params = { page: p, limit: 50 }
      if (isSupport && scopeAll) params.all = 1
      const { data } = await api.get('/tickets', { params })
      const list = Array.isArray(data) ? data : data.data
      setRows(list)
      setHasMore(!Array.isArray(data) && data?.meta?.currentPage < data?.meta?.lastPage)
      setPage(p)
    } catch {
      setError('Errore nel caricamento dei ticket')
    } finally {
      setLoading(false)
    }
  }

  // Initial load + on refreshKey / scope toggle
  useEffect(() => {
    load(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, scopeAll])

  // Apri ticket iniziale se passato via URL (solo una volta)
  useEffect(() => {
    if (initialTicketId && !loading && !initialOpenedRef.current) {
      initialOpenedRef.current = true
      openDetail(initialTicketId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTicketId, loading])

  // Polling lista (ogni 15s) quando il modal NON è aperto
  useEffect(() => {
    if (selected) return
    const id = setInterval(() => load(page), 15000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, page, scopeAll, isSupport])

  const openDetail = async (id) => {
    const params = isSupport && scopeAll ? { all: 1 } : {}
    const { data } = await api.get(`/tickets/${id}`, { params })
    setSelected(data)
    setReply('')
    // appena si monta il thread, scrolla in fondo (prima del paint + robusto)
    rafScrollBottomInstant()
    stickToBottomRef.current = true
  }

  // Polling thread (ogni 5s) finché il modal è aperto
  useEffect(() => {
    if (!selected?.id) return
    let cancelled = false
    const tick = async () => {
      const params = isSupport && scopeAll ? { all: 1 } : {}
      try {
        const { data } = await api.get(`/tickets/${selected.id}`, { params })
        if (cancelled) return
        const prevCount = selected?.messages?.length || 0
        const newCount = data?.messages?.length || 0
        setSelected(data)
        // se arrivano nuovi messaggi ed eri in fondo, resta in fondo
        if (newCount > prevCount && stickToBottomRef.current) {
          rafScrollBottomInstant()
        }
      } catch {}
    }
    const id = setInterval(tick, 5000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, scopeAll, isSupport, selected?.messages?.length])

  // Cambia numero messaggi (anche per invio mio): decidi come scrollare
  useLayoutEffect(() => {
    if (!selected?.id) return
    if (pendingScrollRef.current) {
      rafScrollBottomSmooth()
      pendingScrollRef.current = false
      return
    }
    if (stickToBottomRef.current) {
      rafScrollBottomInstant()
    }
  }, [selected?.messages?.length, selected?.id])

  const sendReply = async () => {
    if (isViewer) return
    if (!reply.trim() || !selected) return
    try {
      setSending(true)
      pendingScrollRef.current = true
      const params = isSupport && scopeAll ? { all: 1 } : {}
      const { data } = await api.post(
        `/tickets/${selected.id}/reply`,
        { message: reply },
        { params }
      )
      setSelected(data)
      setReply('')
      onChanged?.()
      // lo scroll lo farà l'effetto su messages.length (smooth per i miei invii)
    } finally {
      setSending(false)
    }
  }

  const changeStatus = async (newStatus) => {
    if (isViewer || !selected) return
    await api.patch(`/tickets/${selected.id}/status`, { status: newStatus })
    const params = isSupport && scopeAll ? { all: 1 } : {}
    const { data } = await api.get(`/tickets/${selected.id}`, { params })
    setSelected(data)
    onChanged?.()
    if (stickToBottomRef.current) rafScrollBottomInstant()
  }

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim()
    let tmp = rows
    if (s) {
      tmp = tmp.filter(
        (t) =>
          t.subject?.toLowerCase().includes(s) ||
          t.category?.name?.toLowerCase().includes(s) ||
          String(t.id).includes(s)
      )
    }
    if (sort.key && sort.dir) {
      const k = sort.key
      const dir = sort.dir === 'asc' ? 1 : -1
      tmp = [...tmp].sort((a, b) => {
        const av =
          k === 'category'
            ? a.category?.name || ''
            : a[k] ?? a[`${k}At`] ?? a[`${k}_at`]
        const bv =
          k === 'category'
            ? b.category?.name || ''
            : b[k] ?? b[`${k}At`] ?? b[`${k}_at`]
        return (String(av) > String(bv) ? 1 : String(av) < String(bv) ? -1 : 0) * dir
      })
    }
    return tmp
  }, [rows, search, sort])

  // ⬅️ PRIMA: if (loading) return <Loader />
  // ⬆️ Questo smontava il modal e resettava lo scroll.
  // 👉 ORA: mostra il Loader solo se NON hai il modal aperto
  if (loading && !selected) return <Loader />
  if (error) return <div className="tickets-row body error">{error}</div>

  const canChangeSelectedStatus = (sel) =>
    !isViewer && (canStatusAll || sel?.creator?.id === user?.id)

  return (
    <div className="tickets-wrap">
      {/* Topbar */}
      <div className="tickets-topbar">
        <div className="tickets-search-wrap">
          <input
            type="text"
            className="tickets-search"
            placeholder={
              isSupport && scopeAll
                ? 'Cerca (tutti i ticket)…'
                : 'Cerca (i miei ticket)…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Cerca ticket"
          />
        </div>

        {isSupport && (
          <button
            className={`scope-toggle ${scopeAll ? 'is-all' : 'is-mine'}`}
            onClick={() => setScopeAll((s) => !s)}
            aria-pressed={scopeAll}
            aria-label={scopeAll ? 'Vedi i miei ticket' : 'Vedi tutti i ticket'}
          >
            <span className="scope-pill mine">Miei</span>
            <span className="scope-pill all">Tutti</span>
            <span className="scope-thumb" />
          </button>
        )}

        <button
          className="btn btn-outline only-icon"
          onClick={() => load(page)}
          title="Ricarica elenco"
        >
          <FontAwesomeIcon icon={faRotate} />
        </button>
      </div>

      {/* Header + Rows */}
      <div className="tickets-list">
        <div className="tickets-row head" role="row">
          <span onClick={() => toggleSort('id')} className="th-btn">
            ID <FontAwesomeIcon icon={sortIcon('id')} />
          </span>
          <span onClick={() => toggleSort('subject')} className="th-btn">
            Oggetto <FontAwesomeIcon icon={sortIcon('subject')} />
          </span>
          <span onClick={() => toggleSort('category')} className="th-btn">
            Categoria <FontAwesomeIcon icon={sortIcon('category')} />
          </span>
          <span onClick={() => toggleSort('status')} className="th-btn">
            Stato <FontAwesomeIcon icon={sortIcon('status')} />
          </span>
          <span onClick={() => toggleSort('created_at')} className="th-btn">
            Creato <FontAwesomeIcon icon={sortIcon('created_at')} />
          </span>
          <span onClick={() => toggleSort('updated_at')} className="th-btn">
            Agg. <FontAwesomeIcon icon={sortIcon('updated_at')} />
          </span>
          <span>Creato da</span>
          <span className="actions">Azioni</span>
        </div>

        {filtered.map((t) => (
          <div key={t.id} className={rowClassByStatus(t.status)} role="row">
            <span data-label="ID">#{t.id}</span>
            <span className="filename" data-label="Oggetto">
              {t.subject}
            </span>
            <span data-label="Categoria">{t.category?.name || '—'}</span>
            <span data-label="Stato">
              <StatusPill value={t.status} />
            </span>
            <span data-label="Creato">{fmt(t.created_at || t.createdAt)}</span>
            <span data-label="Agg.">{fmt(t.updated_at || t.updatedAt)}</span>
            <span data-label="Creato da">
              {t.creator?.full_name ||
                t.creator?.fullName ||
                (t.creator?.email || (t.creator?.id ? `ID ${t.creator.id}` : '—'))}
            </span>

            <div className="actions" data-label="Azioni">
              <button
                className="btn btn-ghost"
                title="Apri"
                onClick={() => openDetail(t.id)}
              >
                <FontAwesomeIcon icon={faEnvelopeOpenText} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <button className="btn-load-more" onClick={() => load(page + 1)}>
          Carica altri
        </button>
      )}

      {/* Dettaglio + chat */}
      <Modal open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected && (
          <div className="ticket-modal">
            <h3 className="modal-title">
              Ticket #{selected.id} — {selected.subject}
            </h3>

            <div className="ticket-meta">
              Categoria: {selected.category?.name || '—'} •{' '}
              Creato da{' '}
              {selected.creator?.full_name ||
                selected.creator?.fullName ||
                selected.creator?.email ||
                (selected.creator?.id ? `ID ${selected.creator.id}` : '—')}{' '}
              • Creato: {fmt(selected.created_at || selected.createdAt)} • Agg.:{' '}
              {fmt(selected.updated_at || selected.updatedAt)}
            </div>

            <div className="status-row">
              <label>Stato:</label>
              {canChangeSelectedStatus(selected) ? (
                <>
                  <select
                    value={selected.status}
                    onChange={(e) => changeStatus(e.target.value)}
                  >
                    {STATUS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <StatusPill value={selected.status} />
                </>
              ) : (
                <StatusPill value={selected.status} />
              )}
            </div>

            <div
              ref={chatRef}
              className="chat-thread"
              onScroll={onThreadScroll}
              style={{ overflowAnchor: 'none' }}
            >
              {(selected.messages || []).map((m, i) => {
                const senderId = m.sender?.id ?? m.sender_id ?? m.senderId
                const iAmSender = senderId === user?.id
                const senderKind = m.sender_kind || m.senderKind
                const who =
                  m.sender?.full_name ||
                  m.sender?.fullName ||
                  m.sender?.email ||
                  (senderKind === 'staff' ? 'Staff' : 'Utente')

                return (
                  <div
                    key={m.id ?? i}
                    className={`chat-msg ${iAmSender ? 'msg-me' : 'msg-other'}`}
                  >
                    <div className="chat-meta">
                      {who} · {fmt(m.created_at || m.createdAt)}
                    </div>
                    <div className="chat-bubble">{m.body}</div>
                  </div>
                )
              })}
              <div ref={bottomRef} />
            </div>

            <div className="reply-box">
              <textarea
                rows={4}
                placeholder={isViewer ? 'Solo visualizzazione' : 'Scrivi una risposta…'}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                disabled={isViewer}
              />
              <div className="reply-actions">
                <button
                  className="btn btn-primary"
                  onClick={sendReply}
                  disabled={isViewer || sending || !reply.trim()}
                  title={isViewer ? 'Permesso di sola lettura' : 'Invia risposta'}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  <span>{sending ? 'Invio…' : 'Invia risposta'}</span>
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => setSelected(null)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  <span>Chiudi</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
